import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { CARD } from './styles';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
/**
 * The confirmation step for a proposed write.
 *
 * The card is fetched by id rather than rendered from the reply, so what a person approves is the
 * server's record of the proposal, not text the model produced. A reply that describes one change
 * and a proposal that encodes another cannot agree here.
 */
export function LivyProposal(_ref) {
  let proposalId = _ref.proposalId;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    proposal = _useState2[0],
    setProposal = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    busy = _useState6[0],
    setBusy = _useState6[1];
  const load = useCallback(async () => {
    try {
      const _await$livyJson = await livyJson("/proposals/".concat(proposalId)),
        found = _await$livyJson.proposal;
      setProposal(found);
    } catch (e) {
      setError(e.message);
    }
  }, [proposalId]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async verb => {
    setBusy(true);
    setError(null);
    try {
      const result = await livyJson("/proposals/".concat(proposalId, "/").concat(verb), {
        method: 'POST'
      });
      setProposal(p => p ? _objectSpread(_objectSpread({}, p), {}, {
        status: result.status
      }) : p);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (error && !proposal) return null; // a proposal that cannot be loaded is not a thing to approve
  if (!proposal) return null;
  const settled = proposal.status !== 'proposed';
  const expired = proposal.expiresAt ? Date.parse(proposal.expiresAt) <= Date.now() : false;
  return /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, CARD), {}, {
      mt: '14px',
      p: '14px',
      // A proposal that still needs a decision has to look different from one that is settled.
      borderColor: settled ? 'divider' : 'warning.main',
      bgcolor: 'background.paper'
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 10.5,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '6px'
    }
  }, "Proposed change, not applied"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14.5,
      fontWeight: 600,
      mb: '2px'
    }
  }, proposal.summary), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      color: 'text.secondary',
      mb: '12px'
    }
  }, proposal.action, proposal.targetId ? " \xB7 ".concat(proposal.targetId) : ''), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    variant: "outlined",
    sx: {
      mb: '10px',
      fontSize: 12.5
    }
  }, error), settled ? /*#__PURE__*/React.createElement(Alert, {
    severity: proposal.status === 'executed' ? 'success' : 'info',
    variant: "outlined",
    sx: {
      fontSize: 12.5
    }
  }, proposal.status === 'executed' ? 'Applied.' : "No change made (".concat(proposal.status, ").")) : expired ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      fontSize: 12.5
    }
  }, "This proposal expired. Ask again so it can be rebuilt from current data.") : /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    disabled: busy,
    onClick: () => act('confirm'),
    sx: {
      textTransform: 'none'
    }
  }, busy ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 16
  }) : 'Confirm and apply'), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: busy,
    onClick: () => act('reject'),
    sx: {
      textTransform: 'none'
    }
  }, "Discard")));
}

/** Proposal ids the assistant mentioned. Deduped: one card per proposal, however often it is named. */
export const findProposalIds = content => {
  var _content$match;
  return [...new Set((_content$match = content.match(/\bprp_[0-9a-f-]{8,}/gi)) !== null && _content$match !== void 0 ? _content$match : [])];
};